import { DAYS_IN_WEEK, DAY_IN_MILLISECONDS } from "./date.utility.constants";
import { getMidnightDate } from "./get_midnight_date.utility";
import { getRtf } from "./get_rtf";

/**
 * The function will localize and format the date according to the following criteria:
 *  1) Today - Empty string (We don't want to display the date)
 *  2) Last 7 days - Yesterday/ x days ago
 *  3) Current calendar year - "Month(short) day" (e.g. Dec 12)
 *  4) Different year - "dd/mm/yy" (depends on the locale formatting)
 * @param date
 * @param locale
 */
export const getRelativeDate = (date: string, locale = "en-US"): string => {
  // Retrieve one instance of formatter instead of creating it for every call
  const rtf = getRtf(locale);

  // We need to reset the dates to midnight for making a more accurate delta days.
  const d = getMidnightDate(date);
  const currentTime = getMidnightDate();
  const deltaDays = (d.getTime() - currentTime.getTime()) / DAY_IN_MILLISECONDS;

  // Return empty string for today's date.
  if (deltaDays === 0) {
    return "";
  }

  // Return Yesterday/X days ago for the last week
  if (Math.abs(deltaDays) <= DAYS_IN_WEEK) {
    return rtf.format(Math.round(deltaDays), "days");
  }

  // Is in current calendar year
  return d.getFullYear() === currentTime.getFullYear()
    ? d.toLocaleDateString(locale, { month: "short", day: "numeric" })
    : d.toLocaleDateString(locale);
};

/**
 * Formats a given date relative to today's date.
 *
 * @param {Date} d - The date to format.
 * @param {string} [locale="en-US"] - The locale to use for formatting. Defaults to "en-US".
 * @param {number} [delta=1] - The number of days to consider for relative formatting. Defaults to 1.
 *
 * @returns {string} The formatted date. If the date is within `delta` days from today, it returns a relative date string (e.g., "yesterday", "2 days ago").
 * If the date is in the current calendar year but more than `delta` days from today, it returns a date string with the month and day.
 * If the date is not in the current calendar year, it returns a full date string.
 */
export const formatDateRelativeToToday = (
  d: Date,
  locale = "en-US",
  delta = 1
): string => {
  // Retrieve one instance of formatter instead of creating it for every call
  const rtf = getRtf(locale);

  // Normalize both dates to midnight for accurate comparison
  const normalizedDate = getMidnightDate(d.toISOString().split("T")[0]);
  const currentTime = getMidnightDate();
  const deltaDays =
    (normalizedDate.getTime() - currentTime.getTime()) / DAY_IN_MILLISECONDS;

  // Return Yesterday/X days ago for the last week
  if (Math.abs(deltaDays) <= delta) {
    return rtf.format(Math.round(deltaDays), "days");
  }

  // Is in current calendar year
  return d.getFullYear() === currentTime.getFullYear()
    ? d.toLocaleDateString(locale, { month: "short", day: "numeric" })
    : d.toLocaleDateString(locale);
};
